'use client';

import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useState, type CSSProperties } from 'react';

import { ChevronIcon } from '@/components/primitives/icons/ChevronIcon';
import { durations, easeStandard } from '@/motion/tokens';

import styles from './FlywayFilterCarousel.module.css';

const TITLE_SLIDE_PX = 40;
const TITLE_MOTION = { duration: durations.base, ease: easeStandard };

const titleVariants = {
  enter: (direction: 1 | -1) => ({
    opacity: 0,
    x: direction * TITLE_SLIDE_PX,
  }),
  center: { opacity: 1, x: 0 },
  exit: (direction: 1 | -1) => ({
    opacity: 0,
    x: direction * -TITLE_SLIDE_PX,
  }),
};

const reducedTitleVariants = {
  enter: { opacity: 0, x: 0 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 0 },
};

export type FlywayFilterCarouselColors = {
  background: string;
  foreground: string;
};

type FlywayFilterCarouselProps<T extends string> = {
  filters: readonly T[];
  value: T;
  onChange: (filter: T) => void;
  /** Labels aligned by index with `filters`. */
  labels: readonly string[];
  previousLabel: string;
  nextLabel: string;
  /**
   * Per-filter nav colours. When omitted, uses `variant` defaults for `all`
   * and leaves non-`all` to the caller via this callback when provided.
   */
  getColors?: (filter: T) => FlywayFilterCarouselColors;
  /** Default surface when `getColors` is omitted for the active filter. */
  variant?: 'inverse' | 'light';
  className?: string;
  /** Extra data attributes for perf/test hooks (e.g. flyways filter bench). */
  prevDataAttrs?: Record<string, string>;
  nextDataAttrs?: Record<string, string>;
  getTitleDataAttrs?: (filter: T) => Record<string, string>;
};

function defaultColors(
  variant: 'inverse' | 'light',
): FlywayFilterCarouselColors {
  if (variant === 'light') {
    return {
      background: 'var(--color-offwhite-2)',
      foreground: 'var(--text-primary)',
    };
  }
  return {
    background: 'var(--color-ink)',
    foreground: 'var(--text-primary-inverse)',
  };
}

function stepFilter<T extends string>(
  filters: readonly T[],
  value: T,
  delta: 1 | -1,
): T {
  const index = filters.indexOf(value);
  const next =
    (index + delta + filters.length) % filters.length;
  return filters[next] ?? filters[0]!;
}

/**
 * Compact flyway filter carousel — prev/next, sliding title, colour transition.
 * Used by flyways map, chord, and State chart chrome below `lg`.
 */
export function FlywayFilterCarousel<T extends string>({
  filters,
  value,
  onChange,
  labels,
  previousLabel,
  nextLabel,
  getColors,
  variant = 'inverse',
  className,
  prevDataAttrs,
  nextDataAttrs,
  getTitleDataAttrs,
}: FlywayFilterCarouselProps<T>) {
  const reducedMotion = useReducedMotion();
  const [direction, setDirection] = useState<1 | -1>(1);
  const index = filters.indexOf(value);
  const label = labels[index] ?? labels[0] ?? value;

  const colors = getColors?.(value) ?? defaultColors(variant);
  const style = {
    '--nav-bg': colors.background,
    '--nav-fg': colors.foreground,
  } as CSSProperties;

  const go = (delta: 1 | -1) => {
    setDirection(delta);
    onChange(stepFilter(filters, value, delta));
  };

  const navClass = [styles.nav, className].filter(Boolean).join(' ');

  const titleDataAttrs = getTitleDataAttrs?.(value);

  const titleMotionProps = {
    className: styles.title,
    custom: direction,
    variants: reducedMotion ? reducedTitleVariants : titleVariants,
    initial: 'enter' as const,
    animate: 'center' as const,
    exit: 'exit' as const,
    transition: reducedMotion ? { duration: 0 } : TITLE_MOTION,
    ...titleDataAttrs,
  };

  return (
    <div className={navClass} style={style}>
      <button
        type="button"
        className={styles.navButton}
        aria-label={previousLabel}
        onClick={() => go(-1)}
        {...prevDataAttrs}
      >
        <ChevronIcon size={24} className={styles.navButtonIconStart} />
      </button>

      <div className={styles.titleViewport}>
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.p key={value} {...titleMotionProps}>
            {label}
          </motion.p>
        </AnimatePresence>
      </div>

      <button
        type="button"
        className={styles.navButton}
        aria-label={nextLabel}
        onClick={() => go(1)}
        {...nextDataAttrs}
      >
        <ChevronIcon size={24} />
      </button>
    </div>
  );
}
